import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { KeyValuePair } from 'helpers';
import { ItemListResponse, ItemResponse, ISCAutocompleteResponse } from 'helpers/interfaces';
import { TicketQueue, UserType, TicketFilters, TypeInfo, IssueServiceType } from 'helpers/enums';


interface IApplicationData {
    ticketQueues: KeyValuePair<TicketQueue, string>[]
    ticketTypes: ITicketType[]
    ticketPriorities: ITicketPriority[]
    userGroups: KeyValuePair<string, string>[]
    ticketStatuses: ITicketStatus[]
    ticketStatusesNames: string[]
    customerServiceUsers: KeyValuePair<string, string>[]
    assignableUsers: IAssignableUser[]
    userFilters: TicketFilters[]
    issueServiceTypes: KeyValuePair<IssueServiceType, string>[]
    referenceData: IReferenceData[]
}

interface IReporterQuickSearchResponse extends ISCAutocompleteResponse<string> {

}

interface ICustomerQuickSearchResponse extends ISCAutocompleteResponse<string> {

}

interface IProductQuickSearchResponse extends ISCAutocompleteResponse<string> {
    sku: string
    brand: string
    shortDescription: string
    hasSerialNumber: boolean
    suppliers: KeyValuePair<string, string>[]
}

interface IApplicationContext extends IApplicationData {
    isLoading: boolean

    setUserFilters: (v: TicketFilters[]) => void
    searchReporters: (searchText: string, etag: number) => Promise<ItemListResponse<IReporterQuickSearchResponse> | undefined>
    searchCustomers: (searchText: string, etag: number) => Promise<ItemListResponse<ICustomerQuickSearchResponse> | undefined>
    searchProducts: (searchText: string, etag: number) => Promise<ItemListResponse<IProductQuickSearchResponse> | undefined>
}

interface IReferenceData {
    id: string
    key: string
    values: KeyValuePair<string, string>[]
}

interface IAssignableUser {
    email: string
    name: string
    groups?: string[]
    typology: UserType
    canWorkOnAllTicket: boolean
    canWorkOnTicketAssignedToMyGroup: boolean
}

interface ITicketPriority {
    id: string
    description: string
    color: string
}

interface ITicketStatus {
    id: string
    name: string
    customerStatus: string
    isInitialStatus: boolean
    isResolved: boolean
}

interface ITicketType {
    id: string
    name: string
    typeInfo: TypeInfo
}

export let applicationContext = React.createContext({} as IApplicationContext);

let { Provider } = applicationContext;

let ApplicationProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    let [isLoading, setLoading] = useState(true);
    let [ticketTypes, setTicketTypes] = useState<ITicketType[]>([]);
    let [ticketQueues, setTicketQueues] = useState<KeyValuePair<TicketQueue, string>[]>([]);
    let [ticketStatuses, setTicketStatuses] = useState<ITicketStatus[]>([]);
    let [userGroups, setUserGroups] = useState<KeyValuePair<string, string>[]>([]);
    let [customerServiceUsers, setCustomerServiceUsers] = useState<KeyValuePair<string, string>[]>([]);
    let [assignableUsers, setAssignableUsers] = useState<IAssignableUser[]>([]);
    let [ticketPriorities, setTicketPriorities] = useState<ITicketPriority[]>([])
    let [referenceData, setReferenceData] = useState<IReferenceData[]>([])
    let [ticketStatusesNames, setTicketStatusesNames] = useState<string[]>([])
    let [userFilters, setUserFilters] = useState<TicketFilters[]>([])
    let [issueServiceTypes, setIssueServiceTypes] = useState<KeyValuePair<IssueServiceType, string>[]>([])


    useEffect(() => {
        getApplicationData();
    }, [])


    let getApplicationData = async () => {
        try {
            let res = await axios.get<ItemResponse<IApplicationData>>('/api/Application/GetApplicationData');

            let { item } = res.data;
            setTicketTypes(item.ticketTypes);
            setTicketStatuses(item.ticketStatuses);
            setUserGroups(item.userGroups);
            setCustomerServiceUsers(item.customerServiceUsers);
            setAssignableUsers(item.assignableUsers);
            setTicketPriorities(item.ticketPriorities);
            setTicketQueues(item.ticketQueues);
            setReferenceData(item.referenceData)
            setTicketStatusesNames(item.ticketStatusesNames)
            setUserFilters(item.userFilters)
            setIssueServiceTypes(item.issueServiceTypes)

        } catch (err) { }
        finally {
            setLoading(false);
        }
    }

    let searchReporters = async (searchText: string, etag: number) => {
        if (!searchText)
            return undefined;

        try {
            let request = {
                etag,
                searchText
            }

            let res = await axios.get<ItemListResponse<IReporterQuickSearchResponse>>('/api/User/ReportersQuickSearch', { params: request });
            return res.data;
        } catch {
            return undefined;
        }
    }

    let searchCustomers = async (searchText: string, etag: number) => {
        if (!searchText)
            return undefined;

        try {
            let request = {
                etag,
                searchText
            }

            let res = await axios.get<ItemListResponse<ICustomerQuickSearchResponse>>('/api/User/CustomersQuickSearch', { params: request });
            return res.data;
        } catch {
            return undefined;
        }
    }

    let searchProducts = async (searchText: string, etag: number) => {
        if (!searchText)
            return undefined;

        try {
            let request = {
                etag,
                search: searchText
            }

            let res = await axios.get<ItemListResponse<IProductQuickSearchResponse>>('/api/Ticket/SearchProduct', { params: request });
            return res.data;
        } catch {
            return undefined;
        }
    }


    return (
        <Provider
            value={{
                ticketQueues,
                ticketPriorities,
                assignableUsers,
                isLoading,
                ticketTypes,
                userGroups,
                ticketStatuses,
                customerServiceUsers,
                referenceData,
                ticketStatusesNames,
                userFilters,
                issueServiceTypes, 

                setUserFilters,
                searchReporters,
                searchProducts,
                searchCustomers
            }}
        >
            {children}
        </Provider>
    )
}

export default ApplicationProvider;